import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import type { FiscalDocumentDto } from "@finora/types";
import { PaymentMethod } from "@finora/types";
import { PAYMENT_METHOD_LABELS } from "@finora/config";
import { api } from "../../lib/api";
import { CustomerAutocomplete } from "../../components/CustomerAutocomplete";
import { BUYER_ID_TYPES, ID_TYPE_FIELD_LABELS } from "./BuyerSection";

type PfrDocumentType = "PROMET" | "AVANS" | "OBUKA" | "KOPIJA" | "PREDRACUN";

const DOCUMENT_TYPE_LABEL: Record<PfrDocumentType, string> = {
  PROMET: "Промет",
  AVANS: "Аванс",
  OBUKA: "Обука",
  KOPIJA: "Копија",
  PREDRACUN: "Предрачун",
};

// Vidi "Врсте рачуна и трансакција" dokument Poreske uprave koji je
// korisnik priložio. Fiskalizacija je implementirana (PFR invoiceType kôd
// potvrđen ili preuzet iz ranijeg razgovora) samo za PROMET i AVANS -
// ostala tri se mogu izabrati (po zahtevu "mora da ima opcija za izbor
// svega"), ali fiskalizacija za njih vraća jasnu grešku dok kôd ne bude
// potvrđen (vidi pfr-fiscalization.provider.ts).
const FISCALIZATION_READY: PfrDocumentType[] = ["PROMET", "AVANS"];

// Obična (nekorpna) vrsta dokumenta - AVANS je namerno izbačen odavde
// (razgovor 23.09.2026): Аванс-Продаја se više NE pravi menjanjem tipa na
// praznom nacrtu, već posebnim tokom (vidi mini-formu ispod) koji odmah
// pravi NOV dokument sa avansnom stavkom i registrovanom uplatom.
const CART_DOCUMENT_TYPES: PfrDocumentType[] = ["PROMET", "OBUKA", "KOPIJA", "PREDRACUN"];

export interface CreateAdvanceSaleFormInput {
  taxRateId: string;
  amount: string;
  paymentMethod: PaymentMethod;
  payments?: { method: PaymentMethod; amount: string }[];
  note?: string | null;
  /** Kupac je opcion: iz šifarnika (customerId) ili ručno (tip + vrednost + naziv). */
  customerId?: string | null;
  buyerPib?: string | null;
  buyerName?: string | null;
  buyerIdType?: string | null;
  buyerAddress?: string | null;
}

/**
 * Vrsta fiskalnog računa (PROMET je podrazumevano) na TRENUTNOM (korpom
 * punjenom) nacrtu, plus poseban ulaz za Avans-Prodaja.
 *
 * Avans-Prodaja (razgovor 23.09.2026 - "doplata" problem): Poreska uprava
 * zahteva da avansni fiskalni račun bude izdat na STVARNO PRIMLJEN iznos
 * avansa, ne na vrednost budućeg prometa - zato se NE pravi dodavanjem
 * pravih artikala u korpu (to bi predstavilo buduću robu kao da je već
 * prodata). Ovde je poseban mali formular: iznos + poreska stopa + način
 * plaćanja -> pravi se NOV dokument sa jednom "Аванс" stavkom i odmah
 * registrovanom uplatom (createAdvanceSale), spreman za fiskalizaciju u
 * istom koraku. Napomena i dalje ide u "polje za reklamu" (ne šalje se
 * PFR-u), samo se sada unosi PRE kreiranja avansa, ne posle.
 */
export function AdvanceInvoiceSection({
  document: doc,
  onCreateAdvanceSale,
  isCreatingAdvanceSale,
  createAdvanceSaleError,
}: {
  document: FiscalDocumentDto | null;
  onCreateAdvanceSale: (input: CreateAdvanceSaleFormInput) => void;
  isCreatingAdvanceSale?: boolean;
  createAdvanceSaleError?: string | null;
}) {
  const queryClient = useQueryClient();
  const [documentType, setDocumentType] = useState<PfrDocumentType>("PROMET");
  const [note, setNote] = useState("");
  const [showAdvanceForm, setShowAdvanceForm] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [advanceTaxRateId, setAdvanceTaxRateId] = useState("");
  const [advancePaymentMethod, setAdvancePaymentMethod] = useState<PaymentMethod>(PaymentMethod.CASH);
  const [advanceNote, setAdvanceNote] = useState("");
  const [splitAmounts, setSplitAmounts] = useState<Record<string, string>>({});
  const splitMethods = Object.values(PaymentMethod).filter((m) => m !== PaymentMethod.COMBINED);
  const splitEntries = splitMethods
    .filter((m) => Number(splitAmounts[m] || "0") > 0)
    .map((m) => ({ method: m, amount: splitAmounts[m] ?? "0" }));
  const splitTotal = splitEntries.reduce((s, e) => s + Number(e.amount), 0);
  const isCombined = advancePaymentMethod === PaymentMethod.COMBINED;
  const splitOk = !isCombined || (splitEntries.length > 0 && Math.abs(splitTotal - Number(advanceAmount || "0")) < 0.01);
  const [customer, setCustomer] = useState<{ id: string; name: string; taxId: string | null } | null>(null);
  const [manualBuyer, setManualBuyer] = useState(false);
  const [idType, setIdType] = useState("10");
  const [idValue, setIdValue] = useState("");
  const [jbkjs, setJbkjs] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [buyerAddress, setBuyerAddress] = useState("");

  const isSplit = idType === "12";
  const manualOk = isSplit ? Boolean(idValue && jbkjs) : Boolean(idValue);
  const hasManual = manualBuyer && manualOk;

  const { data: taxRates } = useQuery({
    queryKey: ["tax-rates"],
    queryFn: () => api.listTaxRates(),
    enabled: showAdvanceForm,
  });

  useEffect(() => {
    if (doc) {
      setDocumentType(doc.pfrDocumentType && doc.pfrDocumentType !== "AVANS" ? doc.pfrDocumentType : "PROMET");
      setNote(doc.note ?? "");
    }
  }, [doc?.id, doc?.pfrDocumentType, doc?.note]);

  useEffect(() => {
    if (taxRates && taxRates.length > 0 && !advanceTaxRateId) {
      setAdvanceTaxRateId(taxRates.find((t) => t.isDefault)?.id ?? taxRates[0]?.id ?? "");
    }
  }, [taxRates, advanceTaxRateId]);

  const update = useMutation({
    mutationFn: (payload: { pfrDocumentType?: PfrDocumentType; note?: string | null }) =>
      api.updateFiscalDocument(doc!.id, payload),
    onSuccess: (updated) => queryClient.setQueryData(["fiscal-document", updated.id], updated),
  });

  // Avans se može kreirati bez obzira da li kasir već ima prazan nacrt
  // otvoren - kreiraNoviAvans() pravi SVOJ, POTPUNO NOV dokument (vidi
  // PosPage.createAdvanceSale), trenutna korpa (doc) se ne dira.
  const canShowAdvanceButton = !doc || doc.items.length === 0;

  return (
    <div className="mb-3 space-y-3">
      {doc && (
        <div className="rounded-lg border border-border p-3">
          <label className="mb-1 block text-xs text-slate-400">Врста рачуна</label>
          <select
            value={documentType}
            onChange={(e) => {
              const next = e.target.value as PfrDocumentType;
              setDocumentType(next);
              update.mutate({ pfrDocumentType: next });
            }}
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          >
            {CART_DOCUMENT_TYPES.map((key) => (
              <option key={key} value={key}>
                {DOCUMENT_TYPE_LABEL[key]}
              </option>
            ))}
          </select>

          {!FISCALIZATION_READY.includes(documentType) && (
            <p className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-xs text-amber-700">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              Fiskalizacija za "{DOCUMENT_TYPE_LABEL[documentType]}" još nije implementirana (PFR kôd nije
              potvrđen) - pokušaj fiskalizacije će vratiti grešku.
            </p>
          )}

          {doc.linkedAdvanceDocumentId && (
            <div className="mt-2">
              <label className="mb-1 block text-xs text-slate-400">
                Napomena (štampa se u polju za reklamu, ispod računa - ne šalje se PFR-u)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onBlur={() => update.mutate({ note: note || null })}
                rows={2}
                className="w-full rounded-lg border border-border px-2 py-1.5 text-sm focus:border-brand focus:outline-none"
              />
            </div>
          )}
        </div>
      )}

      {canShowAdvanceButton && !showAdvanceForm && (
        <button
          type="button"
          onClick={() => setShowAdvanceForm(true)}
          className="w-full rounded-lg border border-dashed border-border px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
        >
          + Аванс (poseban avansni račun)
        </button>
      )}

      {canShowAdvanceButton && showAdvanceForm && (
        <div className="rounded-lg border border-border p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-medium text-slate-800">Nov avansni račun (Аванс)</p>
            <button
              type="button"
              onClick={() => setShowAdvanceForm(false)}
              className="text-xs text-slate-400 hover:text-slate-600"
            >
              Otkaži
            </button>
          </div>
          <p className="mb-2 text-xs text-slate-500">
            Avansni fiskalni račun se izdaje na stvarno primljen iznos avansa (jedna stavka "Аванс") - ne
            unose se stvarni artikli budućeg prometa, njih ćete uneti na konačnom računu kada avans
            zatvorite.
          </p>

          <label className="mb-1 block text-xs text-slate-400">Iznos avansa</label>
          <input
            inputMode="decimal"
            value={advanceAmount}
            onChange={(e) => setAdvanceAmount(e.target.value)}
            placeholder="0.00"
            className="mb-2 h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />

          <label className="mb-1 block text-xs text-slate-400">Poreska stopa</label>
          <select
            value={advanceTaxRateId}
            onChange={(e) => setAdvanceTaxRateId(e.target.value)}
            className="mb-2 h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          >
            {(taxRates ?? []).map((rate) => (
              <option key={rate.id} value={rate.id}>
                {rate.name} ({rate.ratePercent}%)
              </option>
            ))}
          </select>

          <label className="mb-1 block text-xs text-slate-400">Način plaćanja</label>
          <select
            value={advancePaymentMethod}
            onChange={(e) => setAdvancePaymentMethod(e.target.value as PaymentMethod)}
            className="mb-2 h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          >
            {Object.values(PaymentMethod).map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_LABELS[m]}
              </option>
            ))}
          </select>

          {isCombined && (
            <div className="mb-2 rounded-lg border border-border p-2">
              <p className="mb-1 text-xs font-medium text-slate-600">Iznos po načinu plaćanja</p>
              {splitMethods.map((m) => (
                <div key={m} className="mb-1 flex items-center gap-2">
                  <span className="w-32 text-xs text-slate-600">{PAYMENT_METHOD_LABELS[m]}</span>
                  <input
                    inputMode="decimal"
                    value={splitAmounts[m] ?? ""}
                    onChange={(e) => setSplitAmounts((prev) => ({ ...prev, [m]: e.target.value }))}
                    placeholder="0.00"
                    className="h-8 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                  />
                </div>
              ))}
              <p className={`text-xs ${splitOk ? "text-slate-400" : "text-red-600"}`}>
                Zbir: {splitTotal.toFixed(2)} / avans: {Number(advanceAmount || "0").toFixed(2)}
              </p>
            </div>
          )}

          <div className="mb-2 rounded-lg border border-border p-2">
            <p className="mb-1 text-xs font-medium text-slate-600">Kupac (opciono)</p>
            {customer ? (
              <div className="flex items-center justify-between rounded-lg bg-app-bg px-2 py-1.5 text-sm">
                <span>
                  {customer.name}
                  {customer.taxId && <span className="text-xs text-slate-400"> · {customer.taxId}</span>}
                </span>
                <button type="button" onClick={() => setCustomer(null)} className="text-xs text-slate-400 hover:text-red-600">
                  Ukloni
                </button>
              </div>
            ) : (
              <>
                <CustomerAutocomplete
                  value=""
                  placeholder="Kucajte naziv kupca iz šifarnika..."
                  onSelect={(c) => {
                    setCustomer({ id: c.id, name: c.name, taxId: c.taxId ?? null });
                    setManualBuyer(false);
                  }}
                />
                <button
                  type="button"
                  onClick={() => setManualBuyer((v) => !v)}
                  className="mt-1 text-xs text-brand hover:underline"
                >
                  {manualBuyer ? "Sakrij ručni unos" : "ili unesite kupca ručno"}
                </button>
                {manualBuyer && (
                  <div className="mt-2 space-y-2">
                    <select
                      value={idType}
                      onChange={(e) => {
                        setIdType(e.target.value);
                        if (e.target.value !== "12") setJbkjs("");
                      }}
                      className="h-9 w-full rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none"
                    >
                      {BUYER_ID_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <input
                        value={idValue}
                        onChange={(e) => setIdValue(e.target.value)}
                        placeholder={isSplit ? "PIB" : (ID_TYPE_FIELD_LABELS[idType] ?? "Vrednost")}
                        className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                      />
                      {isSplit && (
                        <input
                          value={jbkjs}
                          onChange={(e) => setJbkjs(e.target.value)}
                          placeholder="JBKJS"
                          className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                        />
                      )}
                    </div>
                    <input
                      value={buyerName}
                      onChange={(e) => setBuyerName(e.target.value)}
                      placeholder="Naziv kupca (opciono)"
                      className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                    />
                    <input
                      value={buyerAddress}
                      onChange={(e) => setBuyerAddress(e.target.value)}
                      placeholder="Adresa (opciono)"
                      className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <label className="mb-1 block text-xs text-slate-400">
            Napomena (štampa se u polju za reklamu - ne šalje se PFR-u)
          </label>
          <textarea
            value={advanceNote}
            onChange={(e) => setAdvanceNote(e.target.value)}
            rows={2}
            placeholder="Obavezno: stvarni naziv artikla koji se avansno prodaje (npr. Avans kamera Hikvision x 2)"
            className="mb-2 w-full rounded-lg border border-border px-2 py-1.5 text-sm focus:border-brand focus:outline-none"
          />

          {createAdvanceSaleError && (
            <p className="mb-2 rounded-lg bg-red-50 px-2 py-1.5 text-xs text-red-700">
              {createAdvanceSaleError}
            </p>
          )}

          <button
            type="button"
            disabled={!advanceNote.trim() || !advanceAmount || Number(advanceAmount) <= 0 || !advanceTaxRateId || (manualBuyer && !customer && !manualOk) || !splitOk || isCreatingAdvanceSale}
            onClick={() =>
              onCreateAdvanceSale({
                taxRateId: advanceTaxRateId,
                amount: advanceAmount,
                paymentMethod: isCombined ? (splitEntries[0]?.method ?? advancePaymentMethod) : advancePaymentMethod,
                ...(isCombined ? { payments: splitEntries } : {}),
                note: advanceNote || null,
                ...(customer
                  ? { customerId: customer.id }
                  : hasManual
                    ? {
                        buyerPib: isSplit ? `${idValue}:${jbkjs}` : idValue,
                        buyerName: buyerName || null,
                        buyerIdType: idType,
                        buyerAddress: buyerAddress || null,
                      }
                    : {}),
              })
            }
            className="h-9 w-full rounded-lg bg-brand text-sm font-medium text-white disabled:opacity-50"
          >
            {isCreatingAdvanceSale ? "Fiskalizujem..." : "Fiskalizuj avans"}
          </button>
        </div>
      )}
    </div>
  );
}
